import { faAt as faAtLight } from '@fortawesome/pro-light-svg-icons';
import { faAt as faAtSolid } from '@fortawesome/pro-solid-svg-icons';
import { cva } from 'class-variance-authority';
import {
  Action,
  Card,
  cn,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  Icon,
  SupportedIcon,
  TextInput,
} from 'corso-admin-ui';
import {
  CrewClaimTypeEnum,
  crewClaimTypeEnumPluralizedName,
  isTruthy,
} from 'corso-types';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCorsoAiChat } from '~/components/ai/CorsoAiChat';
import useIsAdmin from '~/hooks/useIsAdmin';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useMeasurements } from '~/providers/MeasurementsProvider';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import StoreFaviconDisplay from './components/StoreFaviconDisplay';
import useScrollToHash from './hooks/useScrollToHash';
import {
  settingsSearchConfig,
  type SettingsSearchEntry,
} from './settingsSearchConfig';

export const queryParamKey = {
  returnTo: 'return-to',
  settingsNavigation: 'settings-navigation',
} as const;

const sidebarActionVariants = cva(
  'flex items-center gap-x-3 rounded-md p-2 text-xs font-semibold',
  {
    variants: {
      active: {
        [`${false}`]: 'text-neutral-900 hover:bg-neutral-100',
        [`${true}`]: 'bg-neutral-50 text-corso-blue-700',
      },
      kind: {
        button: 'w-full', // special for the button so it fills the width like the other links
      },
    },
    defaultVariants: {
      active: `${false}`,
    },
  },
);

type LinkType = {
  name: string;
  to: string;
  icon: SupportedIcon;
  activeIcon: SupportedIcon;
};

// Widen the as-const config to the runtime interface types.
const searchEntries: readonly SettingsSearchEntry[] = settingsSearchConfig;

const matchesQuery = (q: string, text: string) =>
  text.toLowerCase().includes(q);

const matchesKeywords = (q: string, keywords?: readonly string[]) =>
  keywords?.some((kw) => matchesQuery(q, kw)) ?? false;

// TODO replace with `uniqueBy` from corso-utils
const dedupeByHash = <T extends { hash: string }>(sections: T[]) => {
  const seen = new Set<string>();
  return sections.filter((s) => {
    if (seen.has(s.hash)) return false;
    seen.add(s.hash);
    return true;
  });
};

function filterLinks(links: LinkType[], query: string) {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  return links.flatMap((link) => {
    const config = searchEntries.find((c) => c.to === link.to);
    const isDirectMatch =
      matchesQuery(q, link.name) || matchesKeywords(q, config?.keywords);

    const matchedSections = dedupeByHash(
      [...(config?.sections ?? [])].filter(
        (s) => matchesQuery(q, s.label) || matchesKeywords(q, s.keywords),
      ),
    );

    if (!isDirectMatch && matchedSections.length === 0) return [];
    return [{ link, matchedSections, isDirectMatch }];
  });
}

function SidebarLink({
  to,
  icon,
  activeIcon,
  children,
}: {
  to: Parameters<typeof NavLink>[0]['to'];
  icon: SupportedIcon;
  activeIcon?: SupportedIcon;
  children: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        // Section links (no activeIcon) should never appear active
        sidebarActionVariants({ active: `${isActive && !!activeIcon}` })
      }
    >
      {({ isActive }) => (
        <>
          <Icon icon={isActive && activeIcon ? activeIcon : icon} />
          {children}
        </>
      )}
    </NavLink>
  );
}

function SidebarNav({
  sortedLinks,
  searchResults,
}: {
  sortedLinks: LinkType[];
  searchResults: ReturnType<typeof filterLinks> | null;
}) {
  if (!searchResults) {
    return (
      <ul className="flex flex-col gap-1 p-1">
        {sortedLinks.map((link) => (
          <li key={link.to}>
            <SidebarLink
              to={link.to}
              icon={link.icon}
              activeIcon={link.activeIcon}
            >
              {link.name}
            </SidebarLink>
          </li>
        ))}
      </ul>
    );
  }

  if (searchResults.length === 0) {
    return <p className="p-3 text-xs text-neutral-500">No results found.</p>;
  }

  return (
    <ul className="flex flex-col gap-1 p-1">
      {searchResults.flatMap(({ link, matchedSections, isDirectMatch }) => [
        isDirectMatch && (
          <li key={link.to}>
            <SidebarLink
              to={link.to}
              icon={link.icon}
              activeIcon={link.activeIcon}
            >
              {link.name}
            </SidebarLink>
          </li>
        ),
        ...matchedSections.map((section) => (
          <li key={`${link.to}#${section.hash}`}>
            <SidebarLink
              to={{ pathname: link.to, hash: section.hash }}
              icon={link.icon}
            >
              {section.label}
            </SidebarLink>
          </li>
        )),
      ])}
    </ul>
  );
}

export default function SettingsLayout() {
  const storeUser = useStoreUser();
  const storeName = storeUser.store.name;

  const isAdmin = useIsAdmin();

  const storeId = useStoreId();
  const location = useLocation();
  const returnToSearchParam = useSearchParam(queryParamKey.returnTo);
  const isSettingsNavigation =
    useSearchParam(queryParamKey.settingsNavigation) !== null;
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useScrollToHash();

  // block/bypass underlying vaul focus trap // @see https://github.com/emilkowalski/vaul/issues/496 and https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724
  useLayoutEffect(() => {
    const stopPropagation = (e: FocusEvent) => e.stopImmediatePropagation();
    document.addEventListener('focusin', stopPropagation);
    document.addEventListener('focusout', stopPropagation);

    return () => {
      document.removeEventListener('focusin', stopPropagation);
      document.removeEventListener('focusout', stopPropagation);
    };
  }, []);

  // Clear search when navigating (including hash-only changes from subsection clicks)
  useEffect(() => {
    setSearchQuery('');
  }, [location.pathname, location.hash]);

  const links = useMemo(() => {
    const options: (LinkType | undefined)[] = [
      {
        name: 'Automations',
        to: 'automations',
        icon: 'automation',
        activeIcon: 'automationSolid',
      },
      {
        name: 'Discounts',
        to: 'discounts',
        icon: 'discount',
        activeIcon: 'discountSolid',
      },
      isAdmin
        ? {
            name: 'Data Export',
            to: 'data-export',
            icon: 'download',
            activeIcon: 'download',
          }
        : undefined,
      {
        name: 'Checkout Plus',
        to: 'checkout-plus',
        icon: 'shopNow',
        activeIcon: 'shopNowSolid',
      },
      {
        name: 'Tags',
        to: 'tags',
        icon: 'tags',
        activeIcon: 'tagsSolid',
      },
      {
        name: 'Custom Fields',
        to: 'custom-fields',
        icon: 'customFields',
        activeIcon: 'customFieldsSolid',
      },
      {
        name: 'Metafields',
        to: 'metafields',
        icon: 'product',
        activeIcon: 'productSolid',
      },
      {
        name: 'Email',
        to: 'email',
        icon: faAtLight,
        activeIcon: faAtSolid,
      },
      {
        name: 'Integrations',
        to: 'integrations',
        icon: 'plugins',
        activeIcon: 'pluginsSolid',
      },
      {
        name: 'Notifications',
        to: 'notifications',
        icon: 'bell',
        activeIcon: 'bellSolid',
      },
      {
        name: 'Order Editing',
        to: 'order-editing',
        icon: 'apiKey',
        activeIcon: 'apiKeySolid',
      },
      {
        name: 'Product Groups',
        to: 'product-groups',
        icon: 'history',
        activeIcon: 'historySolid',
      },
      {
        name: 'Reasons',
        to: 'reasons',
        icon: 'dashboard',
        activeIcon: 'dashboardSolid',
      },
      {
        name: 'Registrations',
        to: 'registrations',
        icon: 'registration',
        activeIcon: 'registrationSolid',
      },
      {
        name: crewClaimTypeEnumPluralizedName[CrewClaimTypeEnum.return],
        to: 'returns',
        icon: 'exchange',
        activeIcon: 'exchangeSolid',
      },
      {
        name: 'Tracking',
        to: 'tracking',
        icon: 'shipment',
        activeIcon: 'shipmentSolid',
      },
      {
        name: 'Shipping Claims',
        to: 'shipping-claims',
        icon: 'productGroup',
        activeIcon: 'productGroupSolid',
      },
      {
        name: 'Shipping Plus',
        to: 'shipping-plus',
        icon: 'integration',
        activeIcon: 'integrationSolid',
      },
      {
        name: 'Shipping Policies',
        to: 'shipping-policies',
        icon: 'box',
        activeIcon: 'boxSolid',
      },
      {
        name: 'Theme',
        to: 'theme',
        icon: 'branding',
        activeIcon: 'brandingSolid',
      },
      {
        name: 'Test Drive',
        to: 'test-drive',
        icon: 'testDrive',
        activeIcon: 'testDriveSolid',
      },
      {
        name: 'Users',
        to: 'users',
        icon: 'users',
        activeIcon: 'usersSolid',
      },
      {
        name: 'Developer',
        to: 'developer',
        icon: 'developer',
        activeIcon: 'developerSolid',
      },
      {
        name: crewClaimTypeEnumPluralizedName[CrewClaimTypeEnum.warranty],
        to: 'warranties',
        icon: 'verified',
        activeIcon: 'verifiedSolid',
      },
    ];
    return options.filter(isTruthy);
  }, [isAdmin]);

  const sortedLinks = useMemo(
    () => [...links].sort((a, b) => a.name.localeCompare(b.name)),
    [links],
  );

  const searchResults = useMemo(
    () => (searchQuery.trim() ? filterLinks(sortedLinks, searchQuery) : null),
    [sortedLinks, searchQuery],
  );

  useEffect(() => {
    if (!returnToSearchParam) return; // don't set it if it's empty; always keep the last set value when rendering the layout
    setReturnTo(returnToSearchParam);
  }, [returnToSearchParam]);

  const corsoAiChat = useCorsoAiChat();
  const measurements = useMeasurements();

  const settingsMaxWidth =
    (measurements.get('layout/sidebar')?.width ?? 0) +
    (measurements.get('layout/content')?.width ?? 0);

  return (
    // TODO fix onOpenChange to always navigate to close the drawer
    <Drawer
      open
      onOpenChange={() => navigate(returnTo ?? `/${storeId}`)}
      modal={false}
      autoFocus // with focus trap disable, the drawer should receive focus only when initially opened
    >
      <DrawerContent
        data-vaul-no-drag // https://github.com/emilkowalski/vaul/issues/241
        className={cn(
          'group/drawer h-dvh bg-neutral-100 md:h-(--height)',
          corsoAiChat.open && 'md:max-w-(--max-width)',
        )}
        style={{
          '--max-width': `${settingsMaxWidth}px`,
          '--height': `calc(100vh - ${measurements.get('layout/header')?.height ?? 0}px)`,
        }}
        data-settings-navigation={isSettingsNavigation}
      >
        <DrawerTitle className="sr-only">Settings</DrawerTitle>
        <DrawerDescription className="sr-only">
          Settings navigation.
        </DrawerDescription>
        {/* // ? maybe include a drawer handle */}
        <NavLink
          className="hidden items-center gap-1 p-4 group-data-[settings-navigation=false]/drawer:flex md:group-data-[settings-navigation=false]/drawer:hidden"
          to={{
            pathname: location.pathname,
            search: new URLSearchParams({
              [queryParamKey.settingsNavigation]: 'true', // existence would be enough; i.e. an empty string could work too
            }).toString(),
          }}
        >
          <Icon icon="arrowLeft" />
          Back
        </NavLink>
        <DrawerClose asChild>
          <div className="absolute top-2 right-2 z-10 inline-flex items-center justify-end">
            <Action icon="close" variant="ghost" accessibilityLabel="Close" />
          </div>
        </DrawerClose>
        <div className="mx-auto grid max-w-(--breakpoint-xl) grid-cols-1 gap-4 p-4 md:grid-cols-[minmax(auto,15rem)_1fr] md:p-8">
          <aside className="group-data-[settings-navigation=false]/drawer:hidden md:group-data-[settings-navigation=false]/drawer:block">
            <Card flush>
              <div className="flex items-center gap-3 border-b border-neutral-100 p-3">
                <StoreFaviconDisplay
                  className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-600"
                  storeName={storeName}
                />
                <span
                  className="truncate text-sm font-semibold text-neutral-900"
                  title={storeName}
                >
                  {storeName}
                </span>
              </div>
              <div className="px-3">
                <TextInput
                  id="settings-search"
                  label="Search settings"
                  labelVisuallyHidden
                  placeholder="Search"
                  addon={{
                    insideStart: <Icon icon="search" />,
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <nav>
                <SidebarNav
                  sortedLinks={sortedLinks}
                  searchResults={searchResults}
                />
              </nav>
            </Card>
          </aside>
          <article className="group-data-[settings-navigation=true]/drawer:hidden md:group-data-[settings-navigation=true]/drawer:block">
            <Outlet />
          </article>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
